/**
 * AuditTrailModal.js
 * JobEng — Full audit trail viewer for a single document
 *
 * Props:
 *   visible       boolean — show/hide
 *   onClose       () => void
 *   document      object  — the doc being inspected (uses .id, .name)
 *   companyId     number  — for cache-key scoping and defense-in-depth
 *
 * Path:
 *   app/screens/business/documents/AuditTrailModal.js
 *
 * Backend:
 *   GET /api/docs/:id/audit?limit=50&offset=0&action=...
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View, Text, StyleSheet, Modal, FlatList, TouchableOpacity,
  ActivityIndicator, RefreshControl, Dimensions, Platform,
} from 'react-native';
import { SafeAreaView }           from 'react-native-safe-area-context';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { Ionicons }               from '@expo/vector-icons';
import { LinearGradient }         from 'expo-linear-gradient';
import AsyncStorage               from '@react-native-async-storage/async-storage';
import axios                      from 'axios';

const { width } = Dimensions.get('window');
const IS_PROMAX = width >= 430;
const HPAD      = IS_PROMAX ? 18 : 14;
const CARD_R    = IS_PROMAX ? 16 : 12;
const MODAL_R   = IS_PROMAX ? 24 : 20;
const FS_XS     = IS_PROMAX ? 11 : 10;
const FS_SM     = IS_PROMAX ? 13 : 11;
const FS_BASE   = IS_PROMAX ? 15 : 13;
const FS_MD     = IS_PROMAX ? 16 : 14;
const FS_LG     = IS_PROMAX ? 18 : 16;

const C = {
  primary:'#0e4e96', accent:'#19abb5', gold:'#fdb913',
  success:'#10b981', warning:'#f59e0b', danger:'#dc2626', purple:'#8b5cf6',
  bg:'#f1f5f9', card:'#fff', border:'#e2e8f0',
  dark:'#0f172a', mid:'#475569', light:'#94a3b8',
};

const API_BASE = 'https://api.jobeng.ai';
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'x-api-key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
    'Content-Type': 'application/json',
  },
  timeout: 12000,
});
api.interceptors.request.use(async (config) => {
  try {
    const token = (await AsyncStorage.getItem('auth_token'))
              || (await AsyncStorage.getItem('userToken'))
              || (await AsyncStorage.getItem('token'));
    if (token) config.headers['Authorization'] = `Bearer ${token}`;
    const bizId = await AsyncStorage.getItem('company_id');
    if (bizId) {
      config.params = config.params || {};
      config.params.company_id = parseInt(bizId);
    }
  } catch {}
  return config;
});

// ── Action visual map ───────────────────────────────────────────────────────
const ACTION_META = {
  view:             { icon:'eye',                  color:C.light,   label:'Viewed' },
  preview:          { icon:'eye-outline',          color:C.light,   label:'Previewed' },
  download:         { icon:'download',             color:C.accent,  label:'Downloaded' },
  upload:           { icon:'upload',               color:C.primary, label:'Uploaded' },
  edit:             { icon:'pencil',               color:C.warning, label:'Edited' },
  delete:           { icon:'trash-can-outline',    color:C.danger,  label:'Deleted' },
  approve:          { icon:'check-decagram',       color:C.success, label:'Approved' },
  reject:           { icon:'close-octagon',        color:C.danger,  label:'Rejected' },
  archive:          { icon:'archive-arrow-down',   color:C.mid,     label:'Archived' },
  restore:          { icon:'archive-arrow-up',     color:C.success, label:'Restored' },
  pin:              { icon:'pin',                  color:C.gold,    label:'Pinned' },
  unpin:            { icon:'pin-off',              color:C.light,   label:'Unpinned' },
  favourite:        { icon:'heart',                color:C.danger,  label:'Favourited' },
  unfavourite:      { icon:'heart-off',            color:C.light,   label:'Unfavourited' },
  lock:             { icon:'lock',                 color:C.warning, label:'Locked' },
  unlock:           { icon:'lock-open',            color:C.success, label:'Unlocked' },
  share:            { icon:'share-variant',        color:C.accent,  label:'Shared' },
  duplicate:        { icon:'content-duplicate',    color:C.purple,  label:'Duplicated' },
  version_add:      { icon:'source-branch-plus',   color:C.primary, label:'Added version' },
  version_restore:  { icon:'history',              color:C.warning, label:'Restored version' },
  comment_add:      { icon:'comment-plus-outline', color:C.accent,  label:'Commented' },
  comment_resolve:  { icon:'comment-check-outline',color:C.success, label:'Resolved comment' },
  comment_delete:   { icon:'comment-remove-outline',color:C.danger, label:'Deleted comment' },
  reminder_set:     { icon:'bell-plus-outline',    color:C.warning, label:'Set reminder' },
  reminder_delete:  { icon:'bell-off-outline',     color:C.light,   label:'Deleted reminder' },
  tag_add:          { icon:'tag-plus-outline',     color:C.accent,  label:'Added tag' },
  tag_remove:       { icon:'tag-off-outline',      color:C.light,   label:'Removed tag' },
  acknowledge:      { icon:'check-circle-outline', color:C.success, label:'Acknowledged' },
  sign:             { icon:'draw-pen',             color:C.primary, label:'Signed' },
  link_add:         { icon:'link-plus',            color:C.accent,  label:'Linked' },
  link_remove:      { icon:'link-off',             color:C.light,   label:'Unlinked' },
};
const metaFor = (a) => ACTION_META[a] || { icon:'help-circle-outline', color:C.light, label:a };

const FILTER_GROUPS = [
  { id:'all',       label:'All',       actions:null },
  { id:'access',    label:'Access',    actions:['view','preview','download'] },
  { id:'changes',   label:'Changes',   actions:['edit','upload','version_add','version_restore'] },
  { id:'lifecycle', label:'Lifecycle', actions:['approve','reject','archive','restore','delete'] },
  { id:'sharing',   label:'Sharing',   actions:['share','duplicate','link_add','link_remove'] },
  { id:'comments',  label:'Comments',  actions:['comment_add','comment_resolve','comment_delete'] },
];

const PAGE = 50;

// ── Pretty time ─────────────────────────────────────────────────────────────
const timeAgo = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60)       return 'just now';
  if (diff < 3600)     return `${Math.floor(diff/60)}m ago`;
  if (diff < 86400)    return `${Math.floor(diff/3600)}h ago`;
  if (diff < 7*86400)  return `${Math.floor(diff/86400)}d ago`;
  try { return d.toLocaleDateString() + ' ' + d.toLocaleTimeString().slice(0,5); } catch { return iso; }
};

// ── Row ─────────────────────────────────────────────────────────────────────
const EventRow = React.memo(({ event }) => {
  const m = metaFor(event.action);
  const meta = event.action_meta || {};
  const metaSummary = useMemo(() => {
    if (!meta || Object.keys(meta).length === 0) return null;
    // Pull the most informative 1-2 fields for display
    if (meta.reason)            return `Reason: ${meta.reason}`;
    if (meta.shared_with)       return `→ ${meta.shared_with}`;
    if (meta.new_version)       return `v${meta.new_version}`;
    if (meta.from_version && meta.to_version) return `v${meta.from_version} → v${meta.to_version}`;
    if (meta.tag)               return `#${meta.tag}`;
    if (meta.comment_excerpt)   return `"${String(meta.comment_excerpt).slice(0, 60)}"`;
    if (meta.field_changed)     return `Changed: ${meta.field_changed}`;
    return null;
  }, [meta]);

  return (
    <View style={s.row}>
      <View style={[s.rowIcon, { backgroundColor: `${m.color}18` }]}>
        <MaterialCommunityIcons name={m.icon} size={18} color={m.color} />
      </View>
      <View style={{ flex:1 }}>
        <View style={s.rowHeader}>
          <Text style={s.rowAction} numberOfLines={1}>
            <Text style={{ fontWeight:'700', color:C.dark }}>{event.user_name || `User ${event.user_id}`}</Text>
            <Text style={{ color:C.mid }}>  {String(m.label ?? '').toLowerCase()}</Text>
          </Text>
          <Text style={s.rowTime}>{timeAgo(event.created_at)}</Text>
        </View>
        {!!metaSummary && <Text style={s.rowMeta} numberOfLines={2}>{metaSummary}</Text>}
        {(event.ip_address || event.user_role) && (
          <View style={s.rowFooter}>
            {event.user_role ? <Text style={s.rowSmall}>{event.user_role}</Text> : null}
            {event.user_role && event.ip_address ? <Text style={s.rowDot}>·</Text> : null}
            {event.ip_address ? <Text style={s.rowSmall}>{String(event.ip_address)}</Text> : null}
          </View>
        )}
      </View>
    </View>
  );
});

// ── Main component ─────────────────────────────────────────────────────────
export default function AuditTrailModal({ visible, onClose, document, companyId }) {
  const [events,     setEvents]     = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error,      setError]      = useState(null);
  const [offset,     setOffset]     = useState(0);
  const [hasMore,    setHasMore]    = useState(true);
  const [filter,     setFilter]     = useState('all');
  const [total,      setTotal]      = useState(0);

  const docId = document?.id;

  const fetchPage = useCallback(async (resetting = false) => {
    if (!docId) return;
    if (resetting) { setLoading(true); setError(null); }
    try {
      const params = {
        limit:  PAGE,
        offset: resetting ? 0 : offset,
      };
      if (companyId) params.company_id = companyId;
      const group = FILTER_GROUPS.find(g => g.id === filter);
      if (group?.actions?.length === 1) params.action = group.actions[0];

      const res = await api.get(`/api/docs/${docId}/audit`, { params });
      if (!res.data?.success) throw new Error(res.data?.error || 'Fetch failed');

      let rows = res.data.data || [];
      // client-side filter for multi-action groups (server only does single)
      if (group?.actions && group.actions.length > 1) {
        rows = rows.filter(r => group.actions.includes(r.action));
      }

      setEvents(prev => resetting ? rows : [...prev, ...rows]);
      setOffset(prev => (resetting ? rows.length : prev + rows.length));
      setHasMore(res.data.pagination?.has_more ?? false);
      setTotal(res.data.pagination?.total ?? rows.length);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load audit log');
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, [docId, offset, filter, companyId]);

  // Reset and reload whenever modal opens, doc changes, or filter changes
  useEffect(() => {
    if (visible && docId) {
      setOffset(0); setHasMore(true); setEvents([]);
      fetchPage(true);
    }
  }, [visible, docId, filter]);

  const onRefresh = useCallback(() => {
    setRefreshing(true); setOffset(0); setHasMore(true); fetchPage(true);
  }, [fetchPage]);

  const onEndReached = useCallback(() => {
    if (!loading && !refreshing && hasMore) fetchPage(false);
  }, [loading, refreshing, hasMore, fetchPage]);

  const filtered = events; // server + post-filter already applied

  return (
    <Modal visible={visible} animationType="slide" onRequestClose={onClose} statusBarTranslucent>
      <SafeAreaView style={s.root} edges={['top']}>
        {/* Header */}
        <LinearGradient colors={[C.primary, C.accent]} style={s.header}>
          <TouchableOpacity onPress={onClose} style={s.headerBtn} hitSlop={{ top:10, bottom:10, left:10, right:10 }}>
            <Ionicons name="chevron-back" size={26} color="#fff" />
          </TouchableOpacity>
          <View style={{ flex:1 }}>
            <Text style={s.headerTitle} numberOfLines={1}>Audit Trail</Text>
            <Text style={s.headerSub} numberOfLines={1}>
              {document?.name || 'Document'} · {total} event{total === 1 ? '' : 's'}
            </Text>
          </View>
          <TouchableOpacity onPress={onRefresh} style={s.headerBtn} hitSlop={{ top:10, bottom:10, left:10, right:10 }}>
            <MaterialCommunityIcons name="refresh" size={22} color="#fff" />
          </TouchableOpacity>
        </LinearGradient>

        {/* Filter chips */}
        <View style={s.filterBar}>
          <FlatList
            horizontal
            data={FILTER_GROUPS}
            keyExtractor={g => g.id}
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{ paddingHorizontal:HPAD, gap:8 }}
            renderItem={({ item }) => {
              const active = filter === item.id;
              return (
                <TouchableOpacity
                  style={[s.filterChip, active && s.filterChipActive]}
                  onPress={() => setFilter(item.id)}
                  activeOpacity={0.7}
                >
                  <Text style={[s.filterChipTxt, active && s.filterChipTxtActive]}>{item.label}</Text>
                </TouchableOpacity>
              );
            }}
          />
        </View>

        {/* List */}
        {error ? (
          <View style={s.errBox}>
            <MaterialCommunityIcons name="alert-circle-outline" size={42} color={C.danger} />
            <Text style={s.errTitle}>Couldn't load audit log</Text>
            <Text style={s.errMsg}>{error}</Text>
            <TouchableOpacity style={s.retryBtn} onPress={() => fetchPage(true)} activeOpacity={0.8}>
              <Text style={s.retryBtnTxt}>Retry</Text>
            </TouchableOpacity>
          </View>
        ) : loading && events.length === 0 ? (
          <View style={s.loadBox}>
            <ActivityIndicator size="large" color={C.primary} />
            <Text style={s.loadTxt}>Loading audit history…</Text>
          </View>
        ) : filtered.length === 0 ? (
          <View style={s.emptyBox}>
            <MaterialCommunityIcons name="history" size={56} color={C.light} />
            <Text style={s.emptyTitle}>No audit events yet</Text>
            <Text style={s.emptyMsg}>
              {filter === 'all'
                ? 'Activity on this document will appear here as it happens.'
                : 'No events in this filter. Try another category.'}
            </Text>
          </View>
        ) : (
          <FlatList
            data={filtered}
            keyExtractor={(e, i) => String(e.id || i)}
            renderItem={({ item }) => <EventRow event={item} />}
            contentContainerStyle={{ paddingVertical:8 }}
            refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={C.primary} />}
            onEndReached={onEndReached}
            onEndReachedThreshold={0.4}
            ListFooterComponent={
              loading && events.length > 0
                ? <View style={s.footerLoad}><ActivityIndicator size="small" color={C.primary} /></View>
                : null
            }
            ItemSeparatorComponent={() => <View style={s.sep} />}
          />
        )}
      </SafeAreaView>
    </Modal>
  );
}

const s = StyleSheet.create({
  root:           { flex:1, backgroundColor:C.bg },
  header:         { flexDirection:'row', alignItems:'center', paddingHorizontal:HPAD, paddingTop:Platform.OS === 'android' ? 16 : 8, paddingBottom:14, gap:6 },
  headerBtn:      { width:36, height:36, justifyContent:'center', alignItems:'center' },
  headerTitle:    { fontSize:FS_LG, fontWeight:'700', color:'#fff', letterSpacing:-0.2 },
  headerSub:      { fontSize:FS_SM, color:'rgba(255,255,255,0.85)', marginTop:2 },

  filterBar:      { backgroundColor:C.card, paddingVertical:12, borderBottomWidth:1, borderBottomColor:C.border },
  filterChip:     { paddingHorizontal:14, paddingVertical:8, borderRadius:22, backgroundColor:'#f1f5f9', borderWidth:1, borderColor:C.border },
  filterChipActive: { backgroundColor:`${C.primary}18`, borderColor:C.primary },
  filterChipTxt:  { fontSize:FS_SM, fontWeight:'600', color:C.mid },
  filterChipTxtActive: { color:C.primary, fontWeight:'700' },

  row:            { flexDirection:'row', alignItems:'flex-start', paddingHorizontal:HPAD, paddingVertical:14, backgroundColor:C.card, gap:12 },
  rowIcon:        { width:38, height:38, borderRadius:19, justifyContent:'center', alignItems:'center' },
  rowHeader:      { flexDirection:'row', justifyContent:'space-between', alignItems:'baseline', gap:8 },
  rowAction:      { fontSize:FS_BASE, flex:1 },
  rowTime:        { fontSize:FS_XS, color:C.light, fontWeight:'600' },
  rowMeta:        { fontSize:FS_SM, color:C.mid, marginTop:4, lineHeight:IS_PROMAX?19:17 },
  rowFooter:      { flexDirection:'row', alignItems:'center', gap:6, marginTop:5 },
  rowSmall:       { fontSize:FS_XS, color:C.light },
  rowDot:         { color:C.light, fontSize:FS_XS },
  sep:            { height:1, backgroundColor:'#f8fafc', marginLeft:HPAD + 38 + 12 },
  footerLoad:     { paddingVertical:18, alignItems:'center' },

  loadBox:        { flex:1, justifyContent:'center', alignItems:'center', gap:10, padding:24 },
  loadTxt:        { fontSize:FS_BASE, color:C.mid },

  emptyBox:       { flex:1, justifyContent:'center', alignItems:'center', gap:8, paddingHorizontal:30 },
  emptyTitle:     { fontSize:FS_LG, fontWeight:'700', color:C.dark, marginTop:6 },
  emptyMsg:       { fontSize:FS_BASE, color:C.light, textAlign:'center', lineHeight:IS_PROMAX?22:19 },

  errBox:         { flex:1, justifyContent:'center', alignItems:'center', gap:8, paddingHorizontal:30 },
  errTitle:       { fontSize:FS_LG, fontWeight:'700', color:C.danger, marginTop:6 },
  errMsg:         { fontSize:FS_BASE, color:C.mid, textAlign:'center', lineHeight:IS_PROMAX?22:19, marginBottom:8 },
  retryBtn:       { paddingHorizontal:24, paddingVertical:12, borderRadius:CARD_R, backgroundColor:C.primary },
  retryBtnTxt:    { color:'#fff', fontSize:FS_MD, fontWeight:'700' },
});
